import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Grid, Typography, Link, useTheme, Divider } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import { FOOTER_CONTENT, FooterLink } from '../../constants';
import { getDocsUrl } from '../../../../utils/docsUrl';
import logo from '../../../../../assets/logo.png';

/**
 * Landing page footer with 4-column grid layout
 * Dark background with brand info, quick links, legal links, and contact info
 */
export const LandingFooter: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: '#2D2D2D',
        color: 'white',
        mt: 8,
        pt: 6,
        pb: 4,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Brand Column */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box component="img" src={logo} alt="Lecturo" sx={{ height: 40, width: 40, mr: 1 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Lecturo
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.7 }}>
              {FOOTER_CONTENT.description}
            </Typography>
          </Grid>

          {/* Quick Links Column */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Quick Links
            </Typography>
            <Box component="nav">
              {FOOTER_CONTENT.quickLinks.map((link: FooterLink) =>
                link.external ? (
                  <Link
                    key={link.label}
                    href={getDocsUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'block',
                      color: 'rgba(255, 255, 255, 0.7)',
                      textDecoration: 'none',
                      py: { xs: 1.5, md: 0.5 },
                      minHeight: { xs: 44, md: 'auto' },
                      transition: 'color 0.2s',
                      '&:hover': {
                        color: theme.palette.primary.main,
                      },
                      '&:active': {
                        color: theme.palette.primary.light,
                      },
                    }}
                  >
                    {link.label}
                  </Link>
                ) : link.disabled ? (
                  <Typography
                    key={link.path}
                    component="span"
                    sx={{
                      display: 'block',
                      color: 'rgba(255, 255, 255, 0.3)',
                      py: { xs: 1.5, md: 0.5 },
                      minHeight: { xs: 44, md: 'auto' },
                      cursor: 'not-allowed',
                    }}
                  >
                    {link.label}
                  </Typography>
                ) : (
                  <Link
                    key={link.path}
                    component={RouterLink}
                    to={link.path ?? '/'}
                    sx={{
                      display: 'block',
                      color: 'rgba(255, 255, 255, 0.7)',
                      textDecoration: 'none',
                      py: { xs: 1.5, md: 0.5 },
                      minHeight: { xs: 44, md: 'auto' },
                      transition: 'color 0.2s',
                      '&:hover': {
                        color: theme.palette.primary.main,
                      },
                      '&:active': {
                        color: theme.palette.primary.light,
                      },
                    }}
                  >
                    {link.label}
                  </Link>
                )
              )}
            </Box>
          </Grid>

          {/* Legal Links Column */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Legal
            </Typography>
            <Box component="nav">
              {FOOTER_CONTENT.legalLinks.map((link) => (
                <Link
                  key={link.path}
                  component={RouterLink}
                  to={link.path}
                  sx={{
                    display: 'block',
                    color: 'rgba(255, 255, 255, 0.7)',
                    textDecoration: 'none',
                    py: { xs: 1.5, md: 0.5 },
                    minHeight: { xs: 44, md: 'auto' },
                    transition: 'color 0.2s',
                    '&:hover': {
                      color: theme.palette.primary.main,
                    },
                    '&:active': {
                      color: theme.palette.primary.light,
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Contact Column */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Contact
            </Typography>
            <Link
              href={`mailto:${FOOTER_CONTENT.contact.email}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'rgba(255, 255, 255, 0.7)',
                textDecoration: 'none',
                py: { xs: 1.5, md: 0.5 },
                minHeight: { xs: 44, md: 'auto' },
                transition: 'color 0.2s',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
                '&:active': {
                  color: theme.palette.primary.light,
                },
              }}
            >
              <EmailIcon sx={{ fontSize: 18, mr: 1 }} />
              {FOOTER_CONTENT.contact.email}
            </Link>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 4 }} />

        {/* Copyright */}
        <Typography variant="body2" align="center" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
          {FOOTER_CONTENT.copyright}
        </Typography>
      </Container>
    </Box>
  );
};
